/**
 * Reporting Engine — Phase 14
 * Core service for generating, scheduling, exporting, and managing reports.
 * Supports pre-built templates, custom field selection, and scheduled delivery.
 */

import LocalStorageService from '../storage/LocalStorageService';
import PREBUILT_REPORTS, {
  REPORT_FREQUENCIES,
  FIELD_CATALOG,
  GOVERNMENT_REPORT_TEMPLATES,
  ROLE_DEFAULT_DASHBOARDS,
  EXPORT_FORMATS
} from '../../data/reportTemplates';

class ReportingEngine {
  constructor() {
    this.storageKey = 'reporting_engine';
    this.scheduledKey = 'scheduled_reports';
    this.generatedKey = 'generated_reports';
    this.dashboardConfigKey = 'dashboard_configs';
    this.customReportsKey = 'custom_reports';
    this._ensureStorage();
  }

  _ensureStorage() {
    [this.scheduledKey, this.generatedKey, this.dashboardConfigKey, this.customReportsKey].forEach(key => {
      if (!LocalStorageService.get(key)) {
        LocalStorageService.set(key, []);
      }
    });
  }

  // ─── PRE-BUILT REPORTS ──────────────────────────────────

  getPrebuiltReports() {
    return PREBUILT_REPORTS;
  }

  getPrebuiltByFrequency(frequency) {
    return PREBUILT_REPORTS.filter(r => r.frequency === frequency);
  }

  getPrebuiltByCategory(category) {
    return PREBUILT_REPORTS.filter(r => r.category === category);
  }

  getGovernmentReportTemplates() {
    return GOVERNMENT_REPORT_TEMPLATES;
  }

  getFieldCatalog(module = null) {
    if (module) {
      return FIELD_CATALOG.filter(f => f.module === module);
    }
    return FIELD_CATALOG;
  }

  // ─── REPORT GENERATION ──────────────────────────────────

  async generateReport(templateId, options = {}) {
    try {
      const template = PREBUILT_REPORTS.find(r => r.id === templateId)
        || GOVERNMENT_REPORT_TEMPLATES.find(r => r.id === templateId);

      if (!template) {
        return { success: false, error: `Template '${templateId}' not found` };
      }

      const {
        facilityId = 'all',
        dateRange = this._defaultDateRange(template.frequency),
        filters = {},
        columns = template.defaultColumns || [],
        exportFormat = 'pdf'
      } = options;

      const reportData = await this._collectData(template, facilityId, dateRange, filters);

      const generated = {
        id: `rpt-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        templateId: template.id,
        templateName: template.name,
        frequency: template.frequency,
        category: template.category,
        facilityId,
        dateRange,
        filters,
        columns,
        exportFormat,
        data: reportData,
        rowCount: reportData.length,
        status: 'completed',
        generatedAt: new Date().toISOString(),
        generatedBy: options.userId || 'system'
      };

      LocalStorageService.addItem(this.generatedKey, generated);

      console.info(`[ReportingEngine] Generated report "${template.name}" — ${reportData.length} rows`);
      return { success: true, data: generated };
    } catch (error) {
      console.error('[ReportingEngine] Generation failed:', error);
      return { success: false, error: error.message };
    }
  }

  async generateCustomReport(config) {
    try {
      const {
        name,
        selectedFields = [],
        facilityId = 'all',
        dateRange = {},
        filters = {},
        exportFormat = 'pdf',
        userId
      } = config;

      if (!selectedFields.length) {
        return { success: false, error: 'At least one field is required' };
      }

      const modules = [...new Set(selectedFields.map(fId => {
        const field = FIELD_CATALOG.find(f => f.id === fId);
        return field?.module;
      }).filter(Boolean))];

      const reportData = await this._collectCustomData(modules, selectedFields, facilityId, dateRange, filters);

      const generated = {
        id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        name: name || 'Custom Report',
        isCustom: true,
        selectedFields,
        modules,
        facilityId,
        dateRange,
        filters,
        exportFormat,
        data: reportData,
        rowCount: reportData.length,
        status: 'completed',
        generatedAt: new Date().toISOString(),
        generatedBy: userId || 'system'
      };

      LocalStorageService.addItem(this.generatedKey, generated);
      return { success: true, data: generated };
    } catch (error) {
      console.error('[ReportingEngine] Custom report failed:', error);
      return { success: false, error: error.message };
    }
  }

  // ─── SCHEDULED REPORTS ──────────────────────────────────

  createSchedule(config) {
    try {
      const {
        templateId,
        name,
        frequency,
        recipients = [],
        facilityId = 'all',
        filters = {},
        exportFormat = 'pdf',
        isActive = true,
        userId
      } = config;

      const schedule = {
        id: `sched-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        templateId,
        name: name || 'Scheduled Report',
        frequency,
        recipients,
        facilityId,
        filters,
        exportFormat,
        isActive,
        nextRunAt: this._calculateNextRun(frequency),
        lastRunAt: null,
        runCount: 0,
        createdAt: new Date().toISOString(),
        createdBy: userId || 'system'
      };

      LocalStorageService.addItem(this.scheduledKey, schedule);
      console.info(`[ReportingEngine] Schedule created: "${schedule.name}" (${frequency})`);
      return { success: true, data: schedule };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  updateSchedule(scheduleId, updates) {
    try {
      const updated = LocalStorageService.updateItem(this.scheduledKey, scheduleId, updates);
      if (!updated) {
        return { success: false, error: 'Schedule not found' };
      }
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  deleteSchedule(scheduleId) {
    try {
      LocalStorageService.removeItem(this.scheduledKey, scheduleId);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  getSchedules() {
    return LocalStorageService.get(this.scheduledKey, []);
  }

  async runScheduledReport(scheduleId) {
    try {
      const schedules = this.getSchedules();
      const schedule = schedules.find(s => s.id === scheduleId);
      if (!schedule) {
        return { success: false, error: 'Schedule not found' };
      }

      const result = await this.generateReport(schedule.templateId, {
        facilityId: schedule.facilityId,
        filters: schedule.filters,
        exportFormat: schedule.exportFormat,
        userId: 'scheduler'
      });

      if (result.success) {
        LocalStorageService.updateItem(this.scheduledKey, scheduleId, {
          lastRunAt: new Date().toISOString(),
          runCount: (schedule.runCount || 0) + 1,
          nextRunAt: this._calculateNextRun(schedule.frequency)
        });

        if (schedule.recipients.length > 0) {
          console.info(`[ReportingEngine] Email queued for ${schedule.recipients.length} recipient(s)`);
        }
      }

      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ─── DASHBOARD BUILDER ─────────────────────────────────

  getDashboardConfig(userId, role) {
    const configs = LocalStorageService.get(this.dashboardConfigKey, []);
    const userConfig = configs.find(c => c.userId === userId);
    if (userConfig) return { success: true, data: userConfig };

    const roleDefault = ROLE_DEFAULT_DASHBOARDS[role] || ROLE_DEFAULT_DASHBOARDS.admin;
    return { success: true, data: { userId, role, tiles: roleDefault.tiles, isDefault: true } };
  }

  saveDashboardConfig(userId, tiles) {
    try {
      const configs = LocalStorageService.get(this.dashboardConfigKey, []);
      const existingIndex = configs.findIndex(c => c.userId === userId);
      const config = {
        id: `dash-${userId}`,
        userId,
        tiles,
        isDefault: false,
        updatedAt: new Date().toISOString()
      };

      if (existingIndex >= 0) {
        configs[existingIndex] = config;
      } else {
        configs.push(config);
      }

      LocalStorageService.set(this.dashboardConfigKey, configs);
      return { success: true, data: config };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  getRoleDefaults() {
    return ROLE_DEFAULT_DASHBOARDS;
  }

  // ─── SAVED CUSTOM REPORTS ──────────────────────────────

  saveCustomReportTemplate(config) {
    try {
      const template = {
        id: `custom-tpl-${Date.now()}`,
        ...config,
        createdAt: new Date().toISOString()
      };
      LocalStorageService.addItem(this.customReportsKey, template);
      return { success: true, data: template };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  getCustomReportTemplates() {
    return LocalStorageService.get(this.customReportsKey, []);
  }

  deleteCustomReportTemplate(templateId) {
    try {
      LocalStorageService.removeItem(this.customReportsKey, templateId);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ─── GENERATED REPORT HISTORY ──────────────────────────

  getGeneratedReports(limit = 50) {
    const all = LocalStorageService.get(this.generatedKey, []);
    return all
      .sort((a, b) => new Date(b.generatedAt) - new Date(a.generatedAt))
      .slice(0, limit);
  }

  getGeneratedReport(reportId) {
    const all = LocalStorageService.get(this.generatedKey, []);
    return all.find(r => r.id === reportId) || null;
  }

  // ─── EXPORT ─────────────────────────────────────────────

  exportReport(reportId, format) {
    const report = this.getGeneratedReport(reportId);
    if (!report) {
      return { success: false, error: 'Report not found' };
    }

    if (!EXPORT_FORMATS.includes(format)) {
      return { success: false, error: `Unsupported format: ${format}` };
    }

    let exportedContent;
    switch (format) {
      case 'csv':
        exportedContent = this._toCSV(report);
        break;
      case 'json':
        exportedContent = JSON.stringify(report.data, null, 2);
        break;
      case 'excel':
      case 'pdf':
        exportedContent = this._toCSV(report);
        break;
      default:
        exportedContent = JSON.stringify(report.data);
    }

    this._triggerDownload(exportedContent, `${report.templateName || report.name}_${report.generatedAt.split('T')[0]}`, format);
    return { success: true, format, fileName: `${report.templateName || report.name}.${format}` };
  }

  // ─── PRIVATE HELPERS ────────────────────────────────────

  _defaultDateRange(frequency) {
    const now = new Date();
    const start = new Date(now);

    switch (frequency) {
      case REPORT_FREQUENCIES.DAILY:
        start.setDate(now.getDate() - 1);
        break;
      case REPORT_FREQUENCIES.WEEKLY:
        start.setDate(now.getDate() - 7);
        break;
      case REPORT_FREQUENCIES.MONTHLY:
        start.setMonth(now.getMonth() - 1);
        break;
      case REPORT_FREQUENCIES.QUARTERLY:
        start.setMonth(now.getMonth() - 3);
        break;
      case REPORT_FREQUENCIES.ANNUAL:
        start.setFullYear(now.getFullYear() - 1);
        break;
      default:
        start.setDate(now.getDate() - 30);
    }

    return {
      start: start.toISOString().split('T')[0],
      end: now.toISOString().split('T')[0]
    };
  }

  async _collectData(template, facilityId, dateRange, filters) {
    const rows = [];
    const residentData = this._getMockResidents(facilityId);
    const now = new Date();

    switch (template.id) {
      case 'daily-handover':
        residentData.forEach(r => {
          rows.push({
            resident_name: r.name,
            room_number: r.room || 'N/A',
            wing: r.wing || 'Main Wing',
            status_change: ['Stable', 'Improved', 'Deteriorated', 'New admission'][Math.floor(Math.random() * 4)],
            pending_tasks: Math.floor(Math.random() * 5),
            notes: `Care plan review ${Math.random() > 0.5 ? 'due' : 'completed'}`
          });
        });
        break;

      case 'daily-medication-admin':
        residentData.forEach(r => {
          const meds = ['Paracetamol 500mg', 'Metformin 500mg', 'Atorvastatin 20mg', 'Omeprazole 20mg', 'Amlodipine 5mg'];
          meds.slice(0, Math.floor(Math.random() * 3) + 1).forEach(med => {
            rows.push({
              resident_name: r.name,
              medication_name: med.split(' ')[0],
              medication_dose: med.split(' ')[1],
              administration_time: `${8 + Math.floor(Math.random() * 12)}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
              administered_by: ['Sarah Mitchell RN', 'James Wilson RN', 'Emma Davis EN'][Math.floor(Math.random() * 3)],
              status: Math.random() > 0.05 ? 'Administered' : 'Missed'
            });
          });
        });
        break;

      case 'daily-care-minutes':
      case 'gov-care-minutes-monthly': {
        const wings = ['East Wing', 'West Wing', 'North Wing', 'Memory Support'];
        wings.forEach(wing => {
          const target = 200 + Math.floor(Math.random() * 20);
          const actual = target - 15 + Math.floor(Math.random() * 30);
          const rnTarget = 44;
          const rnActual = rnTarget - 5 + Math.floor(Math.random() * 10);
          rows.push({
            wing,
            care_minutes_target: target,
            care_minutes_actual: actual,
            rn_minutes_target: rnTarget,
            rn_minutes_actual: rnActual,
            compliance: actual >= target ? 'Compliant' : 'Below Target',
            rn_compliance: rnActual >= rnTarget ? 'Compliant' : 'Below Target'
          });
        });
        break;
      }

      case 'daily-occupancy': {
        const wings = ['East Wing', 'West Wing', 'North Wing', 'Memory Support'];
        wings.forEach(wing => {
          const capacity = 30;
          const occupied = capacity - Math.floor(Math.random() * 5);
          rows.push({
            wing,
            capacity,
            occupied,
            available: capacity - occupied,
            occupancy_rate: ((occupied / capacity) * 100).toFixed(1) + '%',
            admissions_today: Math.floor(Math.random() * 2),
            discharges_today: Math.floor(Math.random() * 2)
          });
        });
        break;
      }

      case 'daily-incident-summary':
        for (let i = 0; i < 3 + Math.floor(Math.random() * 5); i++) {
          const resident = residentData[Math.floor(Math.random() * residentData.length)];
          rows.push({
            resident_name: resident?.name || 'Unknown',
            incident_type: ['Fall', 'Medication Error', 'Behavioural', 'Infection', 'Pressure Injury'][Math.floor(Math.random() * 5)],
            incident_severity: ['Low', 'Medium', 'High', 'Critical'][Math.floor(Math.random() * 4)],
            incident_date: now.toISOString().split('T')[0],
            incident_status: ['Open', 'Under Investigation', 'Resolved'][Math.floor(Math.random() * 3)],
            sirs_reportable: Math.random() > 0.7 ? 'Yes' : 'No'
          });
        }
        break;

      case 'daily-resident-movement':
        for (let i = 0; i < Math.floor(Math.random() * 4) + 1; i++) {
          const resident = residentData[Math.floor(Math.random() * residentData.length)];
          rows.push({
            resident_name: resident?.name || 'Unknown',
            room_number: resident?.room || 'N/A',
            movement_type: ['Hospital Transfer', 'Social Leave', 'Respite Out', 'New Admission', 'Returned from Hospital'][Math.floor(Math.random() * 5)],
            date: now.toISOString().split('T')[0],
            expected_return: Math.random() > 0.5 ? 'TBD' : new Date(now.getTime() + 86400000 * Math.floor(Math.random() * 7)).toISOString().split('T')[0]
          });
        }
        break;

      case 'weekly-clinical-review':
        residentData.slice(0, Math.min(residentData.length, 15)).forEach(r => {
          rows.push({
            resident_name: r.name,
            assessments_due: Math.floor(Math.random() * 3),
            care_plan_updates: Math.floor(Math.random() * 2),
            weight_change: (Math.random() * 4 - 2).toFixed(1) + ' kg',
            wound_status: ['None', 'Healing', 'Stable', 'New'][Math.floor(Math.random() * 4)],
            falls_this_week: Math.floor(Math.random() * 2)
          });
        });
        break;

      case 'weekly-waitlist-status':
        for (let i = 0; i < 8; i++) {
          rows.push({
            enquiry_name: `Enquiry ${i + 1}`,
            enquiry_source: ['Website', 'Phone', 'Referral', 'Walk-in'][Math.floor(Math.random() * 4)],
            enquiry_status: ['New', 'Assessment Booked', 'Waitlisted', 'Offer Made', 'Accepted'][Math.floor(Math.random() * 5)],
            waitlist_position: i + 1,
            preferred_room_type: ['Single', 'Shared', 'Suite'][Math.floor(Math.random() * 3)],
            expected_move_in: new Date(now.getTime() + 86400000 * (7 + Math.floor(Math.random() * 30))).toISOString().split('T')[0]
          });
        }
        break;

      case 'weekly-staffing-adequacy': {
        const roles = ['Registered Nurse', 'Enrolled Nurse', 'Personal Care Worker', 'Allied Health', 'Kitchen', 'Maintenance'];
        roles.forEach(role => {
          rows.push({
            staff_role: role,
            rostered_hours: 160 + Math.floor(Math.random() * 40),
            actual_hours: 155 + Math.floor(Math.random() * 50),
            overtime_hours: Math.floor(Math.random() * 20),
            unfilled_shifts: Math.floor(Math.random() * 3),
            agency_hours: Math.floor(Math.random() * 24)
          });
        });
        break;
      }

      case 'monthly-care-statements':
        residentData.forEach(r => {
          rows.push({
            resident_name: r.name,
            assessments_completed: Math.floor(Math.random() * 5) + 1,
            care_plan_goals_met: Math.floor(Math.random() * 4),
            incidents: Math.floor(Math.random() * 2),
            medication_changes: Math.floor(Math.random() * 3),
            activities_attended: Math.floor(Math.random() * 15) + 5,
            family_visits: Math.floor(Math.random() * 8) + 1,
            weight_trend: ['Stable', 'Increasing', 'Decreasing'][Math.floor(Math.random() * 3)]
          });
        });
        break;

      case 'monthly-facility-quality':
        rows.push(
          { indicator: 'Overall Quality Score', value: (85 + Math.random() * 10).toFixed(1) + '%', trend: 'Improving', benchmark: '82%' },
          { indicator: 'Incident Rate (per 1000 bed-days)', value: (2 + Math.random() * 3).toFixed(1), trend: 'Stable', benchmark: '3.5' },
          { indicator: 'Complaint Resolution (within 28 days)', value: (90 + Math.random() * 10).toFixed(0) + '%', trend: 'Improving', benchmark: '85%' },
          { indicator: 'Assessment Compliance', value: (92 + Math.random() * 8).toFixed(0) + '%', trend: 'Stable', benchmark: '95%' },
          { indicator: 'Care Plan Currency', value: (88 + Math.random() * 12).toFixed(0) + '%', trend: 'Improving', benchmark: '90%' },
          { indicator: 'Star Rating (Self-Assessed)', value: (3 + Math.random() * 1.5).toFixed(1), trend: 'Stable', benchmark: '3.5' }
        );
        break;

      case 'monthly-financial-performance':
        rows.push(
          { category: 'Government Subsidy (AN-ACC)', amount: 485000 + Math.floor(Math.random() * 50000), budget: 480000, variance: 'Favourable' },
          { category: 'Resident Contributions', amount: 120000 + Math.floor(Math.random() * 20000), budget: 125000, variance: 'Unfavourable' },
          { category: 'Staffing Costs', amount: -(380000 + Math.floor(Math.random() * 30000)), budget: -375000, variance: 'Unfavourable' },
          { category: 'Medication Costs', amount: -(45000 + Math.floor(Math.random() * 10000)), budget: -48000, variance: 'Favourable' },
          { category: 'Food & Catering', amount: -(32000 + Math.floor(Math.random() * 5000)), budget: -35000, variance: 'Favourable' },
          { category: 'Utilities & Maintenance', amount: -(18000 + Math.floor(Math.random() * 5000)), budget: -20000, variance: 'Favourable' },
          { category: 'Other Operating', amount: -(12000 + Math.floor(Math.random() * 5000)), budget: -15000, variance: 'Favourable' }
        );
        break;

      case 'monthly-infection-control':
        rows.push(
          { infection_type: 'Urinary Tract Infection', active_cases: Math.floor(Math.random() * 3), new_this_month: Math.floor(Math.random() * 2), resolved: Math.floor(Math.random() * 2) },
          { infection_type: 'Respiratory Infection', active_cases: Math.floor(Math.random() * 4), new_this_month: Math.floor(Math.random() * 3), resolved: Math.floor(Math.random() * 2) },
          { infection_type: 'Skin Infection', active_cases: Math.floor(Math.random() * 2), new_this_month: Math.floor(Math.random() * 2), resolved: Math.floor(Math.random() * 3) },
          { infection_type: 'Gastro-intestinal', active_cases: Math.floor(Math.random() * 2), new_this_month: Math.floor(Math.random() * 1), resolved: Math.floor(Math.random() * 2) },
          { metric: 'Hand Hygiene Compliance', value: (90 + Math.random() * 10).toFixed(0) + '%' },
          { metric: 'PPE Compliance', value: (95 + Math.random() * 5).toFixed(0) + '%' }
        );
        break;

      case 'monthly-psychotropic':
        residentData.slice(0, Math.floor(residentData.length * 0.3)).forEach(r => {
          rows.push({
            resident_name: r.name,
            medication: ['Risperidone 0.5mg', 'Olanzapine 5mg', 'Quetiapine 25mg', 'Sertraline 50mg', 'Mirtazapine 15mg'][Math.floor(Math.random() * 5)],
            indication: ['Behavioural symptoms', 'Depression', 'Anxiety', 'Insomnia', 'Psychosis'][Math.floor(Math.random() * 5)],
            last_review: new Date(now.getTime() - 86400000 * Math.floor(Math.random() * 90)).toISOString().split('T')[0],
            next_review_due: new Date(now.getTime() + 86400000 * Math.floor(Math.random() * 90)).toISOString().split('T')[0],
            consent_status: Math.random() > 0.1 ? 'Current' : 'Expired',
            alternatives_documented: Math.random() > 0.2 ? 'Yes' : 'No'
          });
        });
        break;

      case 'monthly-restrictive-practices':
        residentData.slice(0, Math.floor(residentData.length * 0.1) + 1).forEach(r => {
          rows.push({
            resident_name: r.name,
            practice_type: ['Bed rails', 'Lap belt', 'Locked unit', 'Chemical restraint'][Math.floor(Math.random() * 4)],
            authorisation: ['Medical Director', 'GP', 'Tribunal'][Math.floor(Math.random() * 3)],
            start_date: new Date(now.getTime() - 86400000 * Math.floor(Math.random() * 180)).toISOString().split('T')[0],
            review_date: new Date(now.getTime() + 86400000 * Math.floor(Math.random() * 30)).toISOString().split('T')[0],
            alternatives_tried: Math.floor(Math.random() * 4) + 1,
            sirs_notified: Math.random() > 0.5 ? 'Yes' : 'N/A'
          });
        });
        break;

      case 'monthly-complaints-feedback':
        for (let i = 0; i < 5 + Math.floor(Math.random() * 8); i++) {
          rows.push({
            complaint_id: `CMP-${String(i + 1).padStart(4, '0')}`,
            type: ['Care Quality', 'Communication', 'Food', 'Staffing', 'Facilities', 'Billing'][Math.floor(Math.random() * 6)],
            source: ['Family', 'Resident', 'Staff', 'Anonymous'][Math.floor(Math.random() * 4)],
            status: ['Open', 'Under Investigation', 'Resolved', 'Closed'][Math.floor(Math.random() * 4)],
            date_received: new Date(now.getTime() - 86400000 * Math.floor(Math.random() * 30)).toISOString().split('T')[0],
            days_to_resolve: Math.floor(Math.random() * 21) + 1,
            satisfaction: Math.random() > 0.3 ? 'Satisfied' : 'Unsatisfied'
          });
        }
        break;

      case 'quarterly-nqip-review':
      case 'gov-nqip-quarterly':
        rows.push(
          { indicator: 'QI 1 - Pressure injuries (Stage 2+)', value: (1.5 + Math.random() * 2).toFixed(1) + '%', national_avg: '2.8%', trend: 'Improving' },
          { indicator: 'QI 2 - Physical restraint', value: (0.5 + Math.random() * 1).toFixed(1) + '%', national_avg: '1.2%', trend: 'Stable' },
          { indicator: 'QI 3 - Unplanned weight loss', value: (4 + Math.random() * 4).toFixed(1) + '%', national_avg: '6.5%', trend: 'Improving' },
          { indicator: 'QI 4 - Falls (any)', value: (25 + Math.random() * 15).toFixed(0) + '%', national_avg: '32%', trend: 'Stable' },
          { indicator: 'QI 4a - Falls with major injury', value: (0.5 + Math.random() * 1.5).toFixed(1) + '%', national_avg: '1.5%', trend: 'Improving' },
          { indicator: 'QI 5 - Medication management', value: (95 + Math.random() * 5).toFixed(1) + '%', national_avg: '93%', trend: 'Stable' },
          { indicator: 'QI 6 - Hospitalisations', value: (3 + Math.random() * 4).toFixed(1) + '%', national_avg: '5.2%', trend: 'Improving' },
          { indicator: 'QI 7 - Workforce (minutes/resident/day)', value: (200 + Math.random() * 15).toFixed(0), national_avg: '200', trend: 'Stable' },
          { indicator: 'QI 8 - Consumer experience', value: (80 + Math.random() * 15).toFixed(0) + '%', national_avg: '78%', trend: 'Improving' },
          { indicator: 'QI 9 - Quality of life', value: (75 + Math.random() * 15).toFixed(0) + '%', national_avg: '72%', trend: 'Stable' }
        );
        break;

      case 'quarterly-star-rating':
        rows.push(
          { sub_category: 'Residents\' Experience', score: (3 + Math.random() * 1.5).toFixed(1), weight: '33%' },
          { sub_category: 'Compliance', score: (3 + Math.random() * 1.5).toFixed(1), weight: '25%' },
          { sub_category: 'Quality Measures', score: (3 + Math.random() * 1.5).toFixed(1), weight: '25%' },
          { sub_category: 'Staffing', score: (3 + Math.random() * 1.5).toFixed(1), weight: '17%' },
          { sub_category: 'Overall Star Rating', score: (3 + Math.random() * 1.5).toFixed(1), weight: '100%' }
        );
        break;

      case 'quarterly-antimicrobial':
        rows.push(
          { antibiotic: 'Amoxicillin/Clavulanate', prescriptions: Math.floor(Math.random() * 10) + 3, avg_duration_days: (5 + Math.random() * 5).toFixed(1), cultures_ordered: Math.floor(Math.random() * 8) },
          { antibiotic: 'Cefalexin', prescriptions: Math.floor(Math.random() * 8) + 1, avg_duration_days: (5 + Math.random() * 3).toFixed(1), cultures_ordered: Math.floor(Math.random() * 5) },
          { antibiotic: 'Trimethoprim', prescriptions: Math.floor(Math.random() * 12) + 2, avg_duration_days: (3 + Math.random() * 4).toFixed(1), cultures_ordered: Math.floor(Math.random() * 10) },
          { antibiotic: 'Flucloxacillin', prescriptions: Math.floor(Math.random() * 6) + 1, avg_duration_days: (5 + Math.random() * 5).toFixed(1), cultures_ordered: Math.floor(Math.random() * 4) }
        );
        break;

      case 'gov-rn-coverage': {
        const daysInMonth = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
        for (let d = 1; d <= daysInMonth; d++) {
          rows.push({
            date: `${now.getFullYear()}-${String(now.getMonth()).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
            shift_am: 'Covered',
            shift_pm: 'Covered',
            shift_night: Math.random() > 0.05 ? 'Covered' : 'Gap (agency filled)',
            rn_on_duty_am: ['Sarah Mitchell', 'James Wilson'][Math.floor(Math.random() * 2)],
            rn_on_duty_pm: ['Emma Davis', 'Lucy Chen'][Math.floor(Math.random() * 2)],
            rn_on_duty_night: ['Michael Brown', 'Rachel Green'][Math.floor(Math.random() * 2)],
            declaration: 'Compliant'
          });
        }
        break;
      }

      default:
        residentData.forEach(r => {
          rows.push({ resident_name: r.name, status: 'Active', generated: now.toISOString().split('T')[0] });
        });
    }

    return rows;
  }

  async _collectCustomData(modules, selectedFields, facilityId, dateRange, filters) {
    const residents = this._getMockResidents(facilityId);
    return residents.map(r => {
      const row = {};
      selectedFields.forEach(fieldId => {
        const field = FIELD_CATALOG.find(f => f.id === fieldId);
        if (!field) return;
        switch (field.type) {
          case 'number': row[fieldId] = Math.floor(Math.random() * 100); break;
          case 'currency': row[fieldId] = `$${(Math.random() * 10000).toFixed(2)}`; break;
          case 'date': row[fieldId] = new Date().toISOString().split('T')[0]; break;
          default: row[fieldId] = r.name || field.label;
        }
      });
      return row;
    });
  }

  _getMockResidents(facilityId) {
    const names = [
      'Margaret Thompson', 'Harold Jenkins', 'Dorothy Patterson', 'Arthur Williams',
      'Edith Morrison', 'Frank Henderson', 'Betty Cooper', 'George Mitchell',
      'Irene Sullivan', 'Walter Brooks', 'Agnes Foster', 'Roy Campbell',
      'Mabel Stewart', 'Ernest Murray', 'Florence Reed', 'Albert Price',
      'Gladys Watson', 'Stanley Ross', 'Vera Dixon', 'Leonard Harper'
    ];
    return names.map((name, i) => ({
      id: `res-${i + 1}`,
      name,
      room: `${100 + i}`,
      wing: ['East Wing', 'West Wing', 'North Wing', 'Memory Support'][i % 4]
    }));
  }

  _calculateNextRun(frequency) {
    const now = new Date();
    switch (frequency) {
      case 'daily': now.setDate(now.getDate() + 1); break;
      case 'weekly': now.setDate(now.getDate() + 7); break;
      case 'monthly': now.setMonth(now.getMonth() + 1); break;
      case 'quarterly': now.setMonth(now.getMonth() + 3); break;
      case 'annual': now.setFullYear(now.getFullYear() + 1); break;
      default: now.setDate(now.getDate() + 1);
    }
    now.setHours(6, 0, 0, 0);
    return now.toISOString();
  }

  _toCSV(report) {
    if (!report.data || report.data.length === 0) return '';
    const headers = Object.keys(report.data[0]);
    const csvRows = [
      headers.join(','),
      ...report.data.map(row =>
        headers.map(h => {
          const val = String(row[h] ?? '');
          return val.includes(',') ? `"${val}"` : val;
        }).join(',')
      )
    ];
    return csvRows.join('\n');
  }

  _triggerDownload(content, fileName, format) {
    const mimeTypes = { csv: 'text/csv', json: 'application/json', pdf: 'text/csv', excel: 'text/csv' };
    const ext = format === 'excel' ? 'csv' : format;
    const blob = new Blob([content], { type: mimeTypes[format] || 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    console.info(`[ReportingEngine] Downloaded ${fileName}.${ext}`);
  }
}

const reportingEngine = new ReportingEngine();
export default reportingEngine;
